import { DNSRecordType, } from '../types.js';
import { getNameTLD, validateDomainLabel, validateName } from '../utils.js';
import { DNSOverHTTPSResolver } from './dns-resolver.js';
const DEFAULT_DNS_SERVER = 'https://cloudflare-dns.com/dns-query';
const DEFAULT_FORWARDER_DOMAIN = 'vana';
const DEFAULT_REVERSE_LOOKUP_BASE_DOMAIN = 'wallet.vana';
const DEFAULT_LEGACY_REVERSE_LOOKUP_BASE_DOMAIN = 'web3-addr.vana';
export class DNSModule {
    logger;
    cache;
    name = 'DNS';
    #forwarderDomain;
    #reverseLookupBaseDomain;
    #legacyReverseLookupBaseDomain;
    #dnssecVerification;
    #dnsResolver;
    constructor(options, logger, cache) {
        this.logger = logger;
        this.cache = cache;
        this.#forwarderDomain = options?.forwarderDomain ?? DEFAULT_FORWARDER_DOMAIN;
        validateDomainLabel(this.#forwarderDomain);
        this.#reverseLookupBaseDomain = options?.reverseLookupBaseDomain ?? DEFAULT_REVERSE_LOOKUP_BASE_DOMAIN;
        validateName(this.#reverseLookupBaseDomain);
        this.#legacyReverseLookupBaseDomain =
            options?.legacyReverseLookupBaseDomain ?? DEFAULT_LEGACY_REVERSE_LOOKUP_BASE_DOMAIN;
        validateName(this.#legacyReverseLookupBaseDomain);
        this.#dnssecVerification = options?.dnssecVerification ?? true;
        this.#dnsResolver =
            options?.resolver ?? new DNSOverHTTPSResolver({ logger: this.logger, dnsServer: DEFAULT_DNS_SERVER });
    }
    async #resolveForHost(host, name, network) {
        const isTLDRegistered = await this.#isTLDRegistered(name);
        const web3ConnectHostname = isTLDRegistered
            ? `${host}.${name}`
            : `${host}.${name}.${this.#forwarderDomain}`;
        if (isTLDRegistered) {
            this.logger.info(`TLD for ${name} is registered. Performing direct resolution.`);
        }
        else {
            this.logger.info(`TLD for ${name} is not registered. Performing resolution using forwarder domain.`);
        }
        const txtRecords = await this.#dnsResolver.resolve(web3ConnectHostname, DNSRecordType.TXT, this.#dnssecVerification);
        if (txtRecords?.length) {
            const value = this.#getValueFromTXTRecord(txtRecords, network);
            if (value) {
                return {
                    address: value.value,
                    ttl: value.ttl,
                };
            }
        }
    }
    async resolve(name, network) {
        for (const host of ['_w3addr', '_web3connect']) {
            const result = await this.#resolveForHost(host, name, network);
            if (result) {
                return result;
            }
        }
    }
    async reverseResolve(address, network) {
        for (const hostname of [
            `${address}.${network.toLowerCase()}.${this.#reverseLookupBaseDomain}`,
            `${address}.${this.#reverseLookupBaseDomain}`,
        ]) {
            const resolvedName = await this.#reverseResolveForHost(hostname);
            if (resolvedName) {
                return resolvedName;
            }
        }
        return this.#legacyReverseResolve(address, network);
    }
    async #reverseResolveForHost(hostname) {
        const cnameRecords = await this.#dnsResolver.resolve(hostname, DNSRecordType.CNAME, this.#dnssecVerification);
        const record = cnameRecords?.[0];
        if (record) {
            return {
                name: record.value.slice(0, -1),
                ttl: record.ttl,
            };
        }
    }
    async #legacyReverseResolve(address, network) {
        const hostname = `${this.#normalizeAddress(address)}.${this.#legacyReverseLookupBaseDomain}`;
        const txtRecords = await this.#dnsResolver.resolve(hostname, DNSRecordType.TXT, this.#dnssecVerification);
        if (txtRecords?.length) {
            const value = this.#getValueFromTXTRecord(txtRecords, network);
            if (value) {
                return {
                    name: value.value,
                    ttl: value.ttl,
                };
            }
        }
    }
    #normalizeAddress(address) {
        if (!/^\w+$/.test(address)) {
            throw new Error(`Address may contain only alphabet characters and digits: ${address}.`);
        }
        return address.startsWith('0x') ? address.toLowerCase().slice(2) : address.toLowerCase();
    }
    async #isTLDRegistered(name) {
        const tld = getNameTLD(name);
        const cacheKey = `_tld:${tld}`;
        const cachedRegistrationStatus = await this.cache?.get(cacheKey);
        if (cachedRegistrationStatus !== undefined) {
            this.logger.trace(`Returning cached TLD registration status for ${tld} from cache. TLD registered: ${cachedRegistrationStatus}.`);
            return cachedRegistrationStatus === true.toString();
        }
        // Check TLD existing by looking for NS records, which TLD is bound to have
        // DNSSEC verification is always required for TLD check
        const tldNSRecord = await this.#dnsResolver.resolve(tld, DNSRecordType.NS, true);
        const isTLDRegistered = !!tldNSRecord;
        // TLDs are changed very rarely, so we just cache them for one day
        const ONE_DAY_TTL = 60 * 60 * 24;
        await this.cache?.set(cacheKey, isTLDRegistered.toString(), ONE_DAY_TTL);
        return isTLDRegistered;
    }
    #getValueFromTXTRecord(txtRecord, network) {
        const keyValuePairs = this.#joinTXTRecords(txtRecord)
            .split(',')
            .map((pair) => pair.split(/[=:]/));
        const minTTL = Math.min(...txtRecord.map((record) => record.ttl));
        network = network.toUpperCase();
        for (const [key, value] of keyValuePairs) {
            const upperCaseKey = key.toUpperCase();
            if (upperCaseKey === `WALLET.${network}` || upperCaseKey === network) {
                return {
                    value,
                    ttl: minTTL,
                };
            }
        }
        this.logger.info(`No address found for network ${network}.`);
    }
    #joinTXTRecords(text) {
        const t = text
            .map((record) => record.value)
            .join(',')
            .replaceAll('"', '');
        return t;
    }
}
//# sourceMappingURL=dns-module.js.map